/**
 * Client-side cache service for translated assets
 * Uses localStorage for persistence and memory for speed.
 * Survives page reloads — eliminates redundant DeepL API calls.
 *
 * @module ClientCacheService
 */

const STORAGE_KEY = 'sg_translation_cache';
const MAX_MEMORY_SIZE = 100;
const MAX_STORAGE_BYTES = 4 * 1024 * 1024; // 4MB safe limit

class ClientCacheService {
  constructor() {
    this.memoryCache = new Map();
    this.stats = { hits: 0, misses: 0 };

    this._initFromStorage();
    this._scheduleCleanup();
  }

  // --- Public API ---

  /**
   * @param {string|string[]} assetNames
   * @param {string} language
   * @returns {{ data: any, fromCache: true, cachedAt: number } | null}
   */
  get(assetNames, language) {
    const key = this._key(assetNames, language);
    const entry = this.memoryCache.get(key);

    if (entry && this._isValid(entry)) {
      entry.lastAccessed = Date.now();
      this.stats.hits++;
      return { data: entry.data, fromCache: true, cachedAt: entry.timestamp };
    }

    this.stats.misses++;
    return null;
  }

  /**
   * @param {string|string[]} assetNames
   * @param {string} language
   * @param {any} data
   * @param {{ maxAge?: number, priority?: 'high'|'normal' }} options
   */
  set(assetNames, language, data, options = {}) {
    const { maxAge = 30 * 60 * 1000, priority = 'normal' } = options;
    const key = this._key(assetNames, language);
    const now = Date.now();

    this.memoryCache.set(key, {
      data,
      timestamp: now,
      lastAccessed: now,
      maxAge,
      priority,
    });

    this._enforceLimit();
    this._persist();
  }

  has(assetNames, language) {
    const key = this._key(assetNames, language);
    const entry = this.memoryCache.get(key);
    return !!(entry && this._isValid(entry));
  }

  delete(assetNames, language) {
    const key = this._key(assetNames, language);
    if (this.memoryCache.delete(key)) {
      this._persist();
    }
  }

  clearLanguage(language) {
    let count = 0;
    for (const key of [...this.memoryCache.keys()]) {
      if (key.startsWith(`${language}:`)) {
        this.memoryCache.delete(key);
        count++;
      }
    }
    if (count) this._persist();
    return count;
  }

  clear() {
    this.memoryCache.clear();
    this._removeStorage();
  }

  getStats() {
    return {
      entries: this.memoryCache.size,
      hits: this.stats.hits,
      misses: this.stats.misses,
      hitRate: this.stats.hits + this.stats.misses > 0
        ? (this.stats.hits / (this.stats.hits + this.stats.misses)).toFixed(2)
        : '0.00',
    };
  }

  // --- Internal ---

  _key(assetNames, language) {
    const names = Array.isArray(assetNames) ? assetNames.sort().join(',') : assetNames;
    return `${language}:${names}`;
  }

  _isValid(entry) {
    return entry && entry.data && (Date.now() - entry.timestamp) < (entry.maxAge || 30 * 60 * 1000);
  }

  _initFromStorage() {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      Object.entries(parsed).forEach(([key, entry]) => {
        if (this._isValid(entry)) {
          this.memoryCache.set(key, entry);
        }
      });
    } catch {
      // Corrupted storage — ignore
    }
  }

  _persist() {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;

      const obj = {};
      for (const [key, entry] of this.memoryCache.entries()) {
        if (this._isValid(entry)) obj[key] = entry;
      }

      const serialized = JSON.stringify(obj);
      if (serialized.length > MAX_STORAGE_BYTES) {
        // Keep only most-recently-accessed half
        const sorted = [...this.memoryCache.entries()]
          .filter(([, e]) => this._isValid(e))
          .sort((a, b) => b[1].lastAccessed - a[1].lastAccessed)
          .slice(0, Math.floor(this.memoryCache.size / 2));

        const reduced = {};
        sorted.forEach(([k, v]) => { reduced[k] = v; });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(reduced));
      } else {
        localStorage.setItem(STORAGE_KEY, serialized);
      }
    } catch {
      // QuotaExceeded or other — non-critical
    }
  }

  _removeStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* noop */ }
  }

  _enforceLimit() {
    if (this.memoryCache.size <= MAX_MEMORY_SIZE) return;

    const entries = [...this.memoryCache.entries()]
      .sort((a, b) => {
        // Keep high-priority items
        if (a[1].priority !== b[1].priority) {
          return a[1].priority === 'high' ? 1 : -1;
        }
        return a[1].lastAccessed - b[1].lastAccessed; // oldest accessed first
      });

    // Remove oldest
    const toRemove = entries.slice(0, this.memoryCache.size - MAX_MEMORY_SIZE);
    toRemove.forEach(([key]) => this.memoryCache.delete(key));
  }

  _scheduleCleanup() {
    if (typeof window === 'undefined') return;
    setInterval(() => {
      let cleaned = 0;
      for (const [key, entry] of this.memoryCache.entries()) {
        if (!this._isValid(entry)) {
          this.memoryCache.delete(key);
          cleaned++;
        }
      }
      if (cleaned) this._persist();
    }, 10 * 60 * 1000); // every 10 minutes
  }
}

// Singleton
const clientCache = typeof window !== 'undefined' ? new ClientCacheService() : null;

export default clientCache;
export { ClientCacheService };
